import React from "react";
import { useLang } from "../../context/LanguageContext";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { buildWaLink, WHATSAPP_DISPLAY, ADDRESS, LICENSE } from "../../lib/constants";
import { MapPin, Phone, Clock } from "lucide-react";
import { Reveal } from "./Reveal";

export const Contact = () => {
  const { t, lang } = useLang();

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-40 bg-stone-950 text-stone-50 overflow-hidden"
    >
      {/* Decorative */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, #fbbf24 0%, transparent 40%), radial-gradient(circle at 80% 70%, #25D366 0%, transparent 40%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.35em] text-amber-300/90">
                — {t.contact.overline}
              </span>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="mt-6 font-heading text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-stone-50">
                {t.contact.title}
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-8 text-stone-400 font-light leading-relaxed text-lg max-w-xl">
                {t.contact.description}
              </p>
            </Reveal>

            <Reveal delay={220}>
              <a
                href={buildWaLink(
                  lang === "it"
                    ? "Ciao Annette Rooms, vorrei verificare disponibilità e tariffe."
                    : "Hello Annette Rooms, I'd like to check availability and rates."
                )}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-cta"
                className="mt-10 inline-flex items-center gap-4 bg-[#25D366] text-white px-10 py-6 text-sm uppercase tracking-[0.25em] font-medium hover:bg-[#1faa52] transition-all duration-300 shadow-2xl hover:-translate-y-0.5"
              >
                <WhatsAppIcon className="w-6 h-6" />
                {t.contact.cta}
                <span className="text-xs font-normal opacity-80 normal-case tracking-normal">
                  {WHATSAPP_DISPLAY}
                </span>
              </a>
            </Reveal>
          </div>

          <div className="lg:col-span-5 space-y-px bg-stone-800/60 border border-stone-800">
            {[
              {
                icon: Phone,
                label: t.contact.phone,
                value: WHATSAPP_DISPLAY,
                testid: "contact-phone",
              },
              {
                icon: MapPin,
                label: t.contact.address,
                value: ADDRESS,
                testid: "contact-address",
              },
              {
                icon: Clock,
                label: lang === "it" ? "Check-in" : "Check-in",
                value:
                  lang === "it"
                    ? "15:00 – 22:30 · Check-out entro le 10:30"
                    : "3:00 PM – 10:30 PM · Check-out by 10:30 AM",
                testid: "contact-hours",
              },
            ].map((row, i) => {
              const Icon = row.icon;
              return (
                <Reveal key={i} delay={80 * i}>
                  <div
                    data-testid={row.testid}
                    className="bg-stone-950 p-6 md:p-8 flex items-start gap-4"
                  >
                    <Icon className="w-5 h-5 text-amber-300/90 mt-1 stroke-[1.3]" />
                    <div className="flex-1">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500">
                        {row.label}
                      </div>
                      <div className="mt-2 font-heading text-xl md:text-2xl text-stone-50 leading-snug">
                        {row.value}
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
